import { create } from 'zustand';

import service from '@/services/index';
import { deleteObjectField } from '@/utils/object';

import useFeedbackListStore, {
  FeedbackMapT,
} from './Feedback/FeedbackListStore';

type State = {
  tagMap: TagMapT;
};

type Actions = {
  setTagMap: (tagMap: TagMapT) => void;
  deleteTag: (alias: string) => void;
  deleteTagInState: (alias: string) => void;
  changeTagInfo: (alias: string, name: string, color: number) => void;
  changeTagInfoInState: (alias: string, name: string, color: number) => void;
  addTag: (
    parentAlias: string,
    name: string,
    color: number,
    isChildBeingAdded: boolean,
  ) => void;
  addTagInState: (
    parentAlias: string,
    alias: string,
    name: string,
    color: number,
  ) => void;
  fetchAndCreateTagMap: (
    workspaceAlias: string,
    isPublic?: boolean,
  ) => Promise<TagMapT>;
  initTagStore: () => void;
};

const initialState: State = {
  tagMap: {},
};

const useTagStore = create<State & Actions>((set, get) => ({
  ...initialState,
  setTagMap: (tagMap) => {
    set(() => ({ tagMap }));
  },
  deleteTag: (alias) => {
    service.tag.deleteTag(alias);
    get().deleteTagInState(alias);
  },
  deleteTagInState: (alias) => {
    let newTagMap = { ...get().tagMap };
    const deletedTag = newTagMap[alias];
    if (!deletedTag || !deletedTag.parent) return;
    const parent = newTagMap[deletedTag.parent];
    if (!parent) return;
    newTagMap[deletedTag.parent] = {
      ...parent,
      children: parent.children.filter((child) => child !== alias),
    };

    const deletedAliases = [alias];
    let childrenArray = [...newTagMap[alias].children];

    while (childrenArray.length > 0) {
      const child = childrenArray.pop();
      if (!child) break;
      deletedAliases.push(child);
      childrenArray = childrenArray.concat(newTagMap[child].children);
    }

    const { feedbackMap, setState } = useFeedbackListStore.getState();
    const newFeedbackMap = new FeedbackMapT(100);

    deletedAliases.forEach((alias) => {
      newTagMap = deleteObjectField(newTagMap, alias);
      const feedbacks = feedbackMap.cache.values().map((feedback) => {
        if (feedback.labels?.includes(alias)) {
          return {
            ...feedback,
            labels: feedback.labels.filter((tag) => tag !== alias),
          };
        }
        return feedback;
      });
      feedbacks.forEach((feedback) => {
        newFeedbackMap.add(feedback.alias, feedback);
      });
    });
    setState({ feedbackMap: newFeedbackMap });
    get().setTagMap(newTagMap);
  },
  changeTagInfo(alias, name, color) {
    const tagMap = get().tagMap;
    const originalTag = tagMap[alias];
    if (!originalTag) return;
    if (originalTag.name !== name) service.tag.changeTagName(alias, name);
    if (originalTag.color !== color) service.tag.changeTagColor(alias, color);
    get().changeTagInfoInState(alias, name, color);
  },
  changeTagInfoInState(alias, name, color) {
    const newTagMap = { ...get().tagMap };
    newTagMap[alias] = {
      ...newTagMap[alias],
      name: name || newTagMap[alias].name,
      color: color || newTagMap[alias].color,
    };
    get().setTagMap(newTagMap);
  },
  addTag: async (parentAlias, name, color, isChildBeingAdded) => {
    // FIXME(Kyle): Need to fix issue of updating tagMap...
    const data = await service.tag.postTag(parentAlias, name, color);
    const alias = data.alias;
    const newTagMap = { ...get().tagMap };
    const parent = newTagMap[parentAlias];
    if (!parent) return;
    const newTag = {
      alias,
      name,
      color,
      level: parent.level + 1,
      parent: parent.alias,
      children: [],
    };
    newTagMap[alias] = newTag;
    newTagMap[parentAlias] = {
      ...parent,
      children: [...new Set(parent.children.concat([alias]))],
    };
    set((state) => ({ ...state, tagMap: newTagMap }));
  },
  addTagInState: (parentAlias, alias, name, color) => {
    const newTagMap = { ...get().tagMap };
    const parent = newTagMap[parentAlias];
    if (!parent) return;
    const newTag = {
      alias,
      name,
      color,
      level: parent.level + 1,
      parent: parent.alias,
      children: [],
    };
    newTagMap[alias] = newTag;
    newTagMap[parentAlias] = {
      ...parent,
      children: [...new Set(parent.children.concat([alias]))],
    };
    get().setTagMap(newTagMap);
  },
  async fetchAndCreateTagMap(workspaceAlias, isPublic) {
    try {
      const tagMap = await service.tag.fetchTagMap(workspaceAlias, isPublic);
      get().setTagMap(tagMap);

      return tagMap;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  initTagStore() {
    set(initialState);
  },
}));

const DEFAULT_SOURCE_CSV_LABEL = 'CSV/Excel';
const DEFAULT_SOURCE_MANUAL = 'Manual';

export { DEFAULT_SOURCE_CSV_LABEL, DEFAULT_SOURCE_MANUAL };

export default useTagStore;
